import React, { useCallback, useContext, useMemo, useState } from 'react';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import InputBox from '../../atomic/inputbox/InputBox';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import {
  buildKitchenOnboardingFormData,
  updateKitchenOnboardingAPI,
} from '../../context-api/api-services/Onboarding.service';
import MenuFileUpload from './MenuFileUpload';
import './onboarding-comp.scss';

const STEPS = [
  {
    id: 'tax',
    title: 'Business Tax ID',
    description: 'Enter your EIN or TIN as registered with your state.',
  },
  {
    id: 'menu',
    title: 'Upload Your Menu',
    description:
      "Upload JPEG or PNG images of your menu — we'll take care of adding them to your store.",
  },
  {
    id: 'address',
    title: 'Review Your Address',
    description: 'Confirm this is the correct address for your restaurant.',
  },
];

function getApiErrorMessage(response) {
  const body = response?.data;
  if (!body) return 'Failed to submit onboarding. Please try again.';
  if (body.message) return body.message;
  if (body.CustomMessage) return body.CustomMessage;
  if (body.errors?.[0]?.errorMessage) return body.errors[0].errorMessage;
  return 'Failed to submit onboarding. Please try again.';
}

const OnboardingComp = ({ }) => {
  const { onLoadInfoResponse, setOnLoadInfoResponse } = useContext(GlobalStateDataContext);

  const kitchenInfo = onLoadInfoResponse?.data?.data?.data?.KitchenInfo;
  const kitchenName = kitchenInfo?.kitchenName || '';
  const restaurantAddress =
    onLoadInfoResponse?.data?.data?.data?.kitchen_address?.formattedAddress;

  const [currentStep, setCurrentStep] = useState(0);
  const [state, setState] = useState('');
  const [ein, setEin] = useState('');
  const [menuFiles, setMenuFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleMenuFilesChange = useCallback((files) => {
    setMenuFiles(files);
    if (files.length) {
      setErrors((prev) => ({ ...prev, menu: '' }));
    }
  }, []);

  const validateTaxStep = () => {
    const nextErrors = {};
    if (!state.trim()) nextErrors.state = 'State is required';
    if (!ein.trim()) nextErrors.ein = 'EIN is required';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = () => {
    setSubmitError('');

    if (currentStep === 0) {
      if (!validateTaxStep()) return;
      setCurrentStep(1);
      return;
    }

    if (currentStep === 1) {
      if (!menuFiles.length) {
        setErrors({ menu: 'Please select at least one menu file before continuing.' });
        return;
      }
      setErrors({});
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      handleSubmitOnboarding();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setSubmitError('');
      setErrors({});
      setCurrentStep((prev) => prev - 1);
    }
  };

  const markOnboardingCompleted = useCallback(() => {
    setOnLoadInfoResponse((prev) => {
      const kitchenOperationObject = prev?.data?.data?.data?.kitchenOperationObject;
      if (!kitchenOperationObject) return prev;

      return {
        ...prev,
        data: {
          ...prev.data,
          data: {
            ...prev.data.data,
            data: {
              ...prev.data.data.data,
              kitchenOperationObject: {
                ...kitchenOperationObject,
                onborading_status: {
                  ...kitchenOperationObject.onborading_status,
                  ein_menu_uploaded: 'true',
                },
              },
            },
          },
        },
      };
    });
  }, [setOnLoadInfoResponse]);

  const handleSubmitOnboarding = async () => {
    if (!kitchenName) {
      setSubmitError('Kitchen name is missing. Please refresh and try again.');
      return;
    }

    if (!state.trim() || !ein.trim()) {
      setSubmitError('Business tax information is incomplete.');
      setCurrentStep(0);
      return;
    }

    if (!menuFiles.length) {
      setSubmitError('Please add at least one menu file.');
      setCurrentStep(1);
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      const formData = buildKitchenOnboardingFormData({
        kitchenName,
        state: state.trim(),
        federalEin: ein.trim(),
        menuFiles,
      });

      const response = await updateKitchenOnboardingAPI(formData);
      const result = response?.data ?? response;

      if (result?.success) {
        markOnboardingCompleted();
        return;
      }

      setSubmitError(getApiErrorMessage(response));
    } catch (error) {
      setSubmitError(error?.message || 'Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const primaryButtonLabel = useMemo(() => {
    if (currentStep === STEPS.length - 1) {
      return submitting ? 'Submitting…' : 'Complete Setup';
    }
    return 'Next →';
  }, [currentStep, submitting]);

  const renderContent = () => {
    if (currentStep === 0) {
      return (
        <div className='ob-content__fields'>
          <InputBox
            type='text'
            label='State'
            size='small'
            name='state'
            value={state}
            error={!!errors.state}
            inputErrorMsg={errors.state || ''}
            onChange={(e) => {
              setState(e.target.value);
              if (errors.state) setErrors((prev) => ({ ...prev, state: '' }));
            }}
            sx={{ width: '100%', height: 'auto', margin: '0 0 16px' }}
          />
          <InputBox
            type='text'
            label='EIN'
            size='small'
            name='ein'
            value={ein}
            error={!!errors.ein}
            inputErrorMsg={errors.ein || ''}
            onChange={(e) => {
              setEin(e.target.value);
              if (errors.ein) setErrors((prev) => ({ ...prev, ein: '' }));
            }}
            sx={{ width: '100%', height: 'auto', margin: 0 }}
          />
        </div>
      );
    }

    if (currentStep === 1) {
      return (
        <div className='ob-content__upload'>
          {errors.menu && <p className='ob-content__error'>{errors.menu}</p>}
          <MenuFileUpload
            variant='monochrome'
            showUploadButton={false}
            onFilesChange={handleMenuFilesChange}
          />
          {menuFiles.length > 0 && (
            <p className='ob-content__hint ob-content__hint--success'>
              {menuFiles.length} file{menuFiles.length !== 1 ? 's' : ''} ready to submit.
            </p>
          )}
        </div>
      );
    }

    if (currentStep === 2) {
      return (
        <div className='ob-content__address'>
          <div className='ob-address-card'>
            <LocationOnOutlinedIcon className='ob-address-card__icon' />
            <div>
              <p className='ob-address-card__label'>Restaurant address</p>
              <p className='ob-address-card__value'>
                {restaurantAddress || 'No address on file'}
              </p>
            </div>
          </div>
          <p className='ob-content__hint'>
            If this address looks incorrect, please contact support to update it.
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <div className='ob-page'>
      <div className='ob-card'>
        <aside className='ob-sidebar'>
          <div className='ob-sidebar__top'>
            <h2 className='ob-sidebar__heading'>Account Onboarding</h2>
            <p className='ob-sidebar__sub'>
              Fill in the details below. After review, an account manager will hand-deliver your
              tablet and guide you through setup.
            </p>
          </div>

          <nav className='ob-nav'>
            {STEPS.map((step, index) => {
              const isCompleted = index < currentStep;
              const isActive = index === currentStep;
              const isLast = index === STEPS.length - 1;

              return (
                <div key={step.id} className='ob-nav-row'>
                  <div className='ob-nav-rail'>
                    <div
                      className={[
                        'ob-nav-dot',
                        isActive ? 'ob-nav-dot--active' : '',
                        isCompleted ? 'ob-nav-dot--done' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {isCompleted ? '✓' : index + 1}
                    </div>
                    {!isLast && <div className='ob-nav-line' />}
                  </div>
                  <div className='ob-nav-text'>
                    <span
                      className={[
                        'ob-nav-label',
                        isActive ? 'ob-nav-label--active' : '',
                        isCompleted ? 'ob-nav-label--done' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {step.title}
                    </span>
                  </div>
                </div>
              );
            })}
          </nav>
        </aside>

        <main className='ob-main'>
          <div className='ob-main__inner'>
            <header className='ob-main__header'>
              <span className='ob-main__step-tag'>
                Step {currentStep + 1} of {STEPS.length}
              </span>
              <h1 className='ob-main__title'>{STEPS[currentStep].title}</h1>
              <p className='ob-main__desc'>{STEPS[currentStep].description}</p>
            </header>

            <div className='ob-main__content'>{renderContent()}</div>
          </div>

          <footer className='ob-actions'>
            {submitError && <p className='ob-actions__error'>{submitError}</p>}
            {currentStep > 0 && (
              <button
                type='button'
                className='ob-btn ob-btn--secondary'
                onClick={handleBack}
                disabled={submitting}
              >
                ← Back
              </button>
            )}
            <button
              type='button'
              className='ob-btn ob-btn--primary'
              onClick={handleNext}
              disabled={submitting}
            >
              {primaryButtonLabel}
            </button>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default OnboardingComp;
